/**
 * Canal Shopee: resumo da loja, anúncios (com o vínculo e o preço/estoque da Berytus), publicar produtos e publicações.
 * Preço e estoque e Configurações do canal usam as mesmas telas do Mercado Livre (com a loja da Shopee).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { Download, ExternalLink, RefreshCw, Search, Send, Sparkles, Wrench, X } from 'lucide-react';
import { useMe } from '../components/AuthGate.tsx';
import { DuplicatesButton } from '../components/Duplicates.tsx';
import { ChannelHome } from './ChannelHome.tsx';
import { Modal } from './CatalogSettings.tsx';
import { api, brl, dateTime, int } from '../api.ts';
import { ErrorBox, Loading, Tag, Thumb } from '../components/ui.tsx';
import { Head, LISTING_ALERT, Pager, TITLE_ALERTS, useSearchBox, useUrlState } from './MeliChannel.tsx';
import type { ListingAlert } from '@berytus/contracts';
import { ListingCategorySelect, categoryParams } from '../components/ListingCategory.tsx';
import { AttrFilterButton } from '../components/AttrFilter.tsx';
import { ClassSelect } from './OwnListings.tsx';
import { pickAccount, useSelectedAccountId } from '../components/AccountPicker.tsx';
import { can } from '@berytus/contracts';

interface ShopeeAccount {
  id: string; name: string | null; shopId: string; status: string; lastError: string | null;
  region: string | null; env: 'test' | 'live' | null; priceFactor: number; lastPullAt: string | null;
  syncMode: 'off' | 'stock' | 'price' | 'price_stock'; stockPct: number; lastSyncAt: string | null; lastSyncStats: { sent?: number; failed?: number } | null;
  pull: { id: string; status: string; stats: { items?: number; reconcile?: { linked: number; orphan: number } }; error: string | null; finished_at: string | null } | null;
  listings: { total: number; active: number; paused: number; linked: number; unlinked: number };
  publications: { pending: number; published: number; failed: number };
  pushErrors: number;
  orders: { total: number; lastAt: string | null; syncedAt: string | null };
  titleAlerts: number;
  orders7: { n: number; revenue: number; to_ship: number };
  products: { active: number; with_listing: number; without_listing: number; without_in_stock: number };
}

const SYNC: Record<ShopeeAccount['syncMode'], string> = { off: 'desligado', stock: 'só estoque', price: 'só preço', price_stock: 'preço e estoque' };
const LISTING_STATUS: Record<string, { label: string; tone: string }> = {
  active: { label: 'Ativo', tone: 'ok' }, paused: { label: 'Fora do ar', tone: 'off' }, under_review: { label: 'Em revisão / banido', tone: 'warn' },
};
const PUB_STATUS: Record<string, { label: string; tone: string }> = {
  queued: { label: 'Na fila', tone: 'info' }, sending: { label: 'Enviando', tone: 'info' }, published: { label: 'Publicado', tone: 'ok' },
  failed: { label: 'Recusado', tone: 'err' }, cancelled: { label: 'Cancelado', tone: 'off' },
};

function useShopee() {
  const q = useQuery({
    queryKey: ['shopee', 'overview'],
    queryFn: () => api<{ configured: boolean; accounts: ShopeeAccount[] }>('/channels/shopee/overview'),
    refetchInterval: (query) => (query.state.data?.accounts.some((a) => a.pull?.status === 'queued' || a.pull?.status === 'running' || a.publications.pending > 0) ? 3000 : false),
  });
  const selected = useSelectedAccountId('shopee');
  const account = pickAccount(q.data?.accounts, selected);
  return { q, account };
}

function NoShop() {
  return <div className="panel empty"><b>Nenhuma loja da Shopee conectada</b><Link to="/canais">Conecte a loja em Integração de canais.</Link></div>;
}

function TestNotice({ a }: { a: ShopeeAccount }) {
  if (a.env !== 'test') return null;
  return (
    <div className="notice warn small">
      Loja de <b>teste</b> da Shopee (sandbox{a.region ? `, região ${a.region}` : ''}). Nada aqui aparece para compradores.
      {a.priceFactor !== 1 && <> Como a loja de teste não é do Brasil, os preços vão para a Shopee multiplicados por {int(a.priceFactor)} (moeda local) e voltam divididos: nas telas da Berytus tudo continua em reais.</>}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Resumo
// ---------------------------------------------------------------------------------------------

export function ShopeeSummary() {
  const { q, account: a } = useShopee();
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ['shopee'] });
  const pull = useMutation({ mutationFn: (id: string) => api(`/channels/shopee/accounts/${id}/pull`, { method: 'POST' }), onSuccess: refresh });
  const push = useMutation({ mutationFn: (id: string) => api(`/channels/accounts/${id}/sync/run`, { method: 'POST' }), onSuccess: refresh });
  const orders = useMutation({ mutationFn: (id: string) => api(`/channels/shopee/accounts/${id}/orders/pull`, { method: 'POST', json: {} }), onSuccess: () => setTimeout(refresh, 4000) });
  const pulling = a?.pull?.status === 'queued' || a?.pull?.status === 'running';
  const items = a?.pull?.stats?.items;

  if (q.isPending) return <><Head title="Shopee" /><Loading /></>;
  if (q.error) return <><Head title="Shopee" /><ErrorBox error={q.error} /></>;
  if (!a) return <><Head title="Shopee" /><NoShop /></>;
  const l = a.listings;
  const review = l.unlinked + a.titleAlerts;
  const p = a.products;

  // Cards de anúncios (uma seção só; o vínculo com o catálogo entra em "Para revisar" e "Produtos anunciados").
  const listings = (
    <div className="kpis">
      <Link className="kpi" to="/canal/shopee/anuncios?status=active">
        <span className="k-label">Anúncios ativos</span>
        <span className="k-value">{int(l.active)}</span>
        <span className="k-note">{int(l.total)} anúncios na loja</span>
      </Link>
      <Link className="kpi" to="/canal/shopee/anuncios?status=paused">
        <span className="k-label">Fora do ar</span>
        <span className="k-value">{int(l.paused)}</span>
        <span className="k-note">{int(Math.max(0, l.total - l.active - l.paused))} em outras situações</span>
      </Link>
      <Link className={`kpi${review > 0 ? ' alert' : ''}`} to={l.unlinked > 0 ? '/canal/shopee/anuncios?link=unlinked' : '/canal/shopee/anuncios'}>
        <span className="k-label">Anúncios para revisar</span>
        <span className="k-value">{int(review)}</span>
        <span className="k-note">{int(l.unlinked)} sem vínculo · {int(a.titleAlerts)} com título diferente · {int(l.linked)} vinculados</span>
      </Link>
      <Link className="kpi" to="/canal/shopee/anuncios?link=linked">
        <span className="k-label">Produtos anunciados</span>
        <span className="k-value">{int(p.with_listing)}</span>
        <span className="k-note">de {int(p.active)} SKUs ativos ({((100 * p.with_listing) / (p.active || 1)).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%)</span>
      </Link>
      <Link className={`kpi${p.without_in_stock > 0 ? ' alert' : ''}`} to="/canal/shopee/publicar">
        <span className="k-label">Com estoque e sem anúncio</span>
        <span className="k-value">{int(p.without_in_stock)}</span>
        <span className="k-note">{int(p.without_listing)} SKUs ativos sem anúncio (faltam anunciar)</span>
      </Link>
    </div>
  );

  return (
    <>
      <Head title={`Shopee · ${a.name ?? `Loja ${a.shopId}`}`}>
        <p>
          {pulling
            ? `Lendo os anúncios da loja${items ? ` (${int(items)} até agora)` : ''}…`
            : a.pull?.finished_at
              ? `Anúncios lidos em ${dateTime(a.pull.finished_at)}`
              : 'Os anúncios ainda não foram lidos'}
          {' · '}Pedidos lidos até {a.orders.syncedAt ? dateTime(a.orders.syncedAt) : '—'} (novos a cada 5 minutos)
          {' · '}Envio de preço e estoque: {SYNC[a.syncMode]}, {int(a.stockPct)}% do estoque
          {a.lastSyncAt ? `, último em ${dateTime(a.lastSyncAt)}${a.lastSyncStats?.sent ? ` (${int(a.lastSyncStats.sent)})` : ''}` : ''}
          {a.publications.pending > 0 && <> · {int(a.publications.pending)} publicaç{a.publications.pending === 1 ? 'ão' : 'ões'} na fila</>}
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
          <button className="btn sm" type="button" disabled={a.status !== 'active' || pulling || pull.isPending} onClick={() => pull.mutate(a.id)}>
            <RefreshCw size={14} /> {pulling ? 'Lendo…' : 'Ler anúncios agora'}
          </button>
          <button className="btn sm" type="button" disabled={orders.isPending} onClick={() => orders.mutate(a.id)}>
            {orders.isSuccess ? 'Buscando pedidos…' : 'Buscar pedidos agora'}
          </button>
          <button className="btn sm" type="button" disabled={a.syncMode === 'off' || push.isPending} onClick={() => push.mutate(a.id)}
            title={a.syncMode === 'off' ? 'Ligue o envio em Configurações do canal → Envio ao canal' : undefined}>
            {push.isSuccess ? 'Envio na fila' : 'Enviar preço e estoque agora'}
          </button>
        </div>
      </Head>
      {(pull.error || push.error || orders.error) && <div style={{ marginBottom: 12 }}><ErrorBox error={pull.error ?? push.error ?? orders.error} /></div>}

      <ChannelHome channel="shopee" listings={listings} notices={<>
        <TestNotice a={a} />
        {a.status !== 'active' && (
          <div className="notice err">A autorização da loja venceu ou foi revogada na Shopee. <Link to="/canais">Reconectar</Link>.</div>
        )}
        {a.lastError && a.status === 'active' && <div className="notice err small">{a.lastError}</div>}
        {a.pull?.status === 'failed' && <div className="notice err">A última leitura falhou: {a.pull.error}</div>}
        {a.pushErrors > 0 && (
          <div className="notice err small">{int(a.pushErrors)} envio{a.pushErrors === 1 ? '' : 's'} de preço e estoque com erro. <Link to="/canal/shopee/configuracoes?aba=envio">Ver os erros</Link></div>
        )}
        {a.syncMode === 'off' && (
          <div className="notice small">
            O envio está desligado: a Berytus não mexe em preço nem estoque na Shopee. Para ligar: <Link to="/canal/shopee/configuracoes?aba=estoque">% do estoque</Link> e <Link to="/canal/shopee/configuracoes?aba=envio">Envio ao canal</Link>.
          </div>
        )}
      </>} />
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Anúncios
// ---------------------------------------------------------------------------------------------

interface ListingRow {
  alerts: ListingAlert[]; titleAlertDetail: string | null;
  id: string; itemId: string; modelId: string | null; title: string; sellerSku: string | null; ean: string | null; status: string;
  price: number | null; stock: number | null; thumbnail: string | null; link: string;
  product: { id: string; sku: string; title: string | null } | null;
  berytus: { price: number | null; stock: number | null; blocked: string | null; error: string | null };
}

export function ShopeeListings() {
  const { q: ov, account: a } = useShopee();
  const [params, set] = useUrlState();
  const [search, setSearch] = useSearchBox(params, set);
  const page = Number(params.get('page') ?? 1);
  const link = params.get('link') ?? 'all';
  const q = useQuery({
    queryKey: ['shopee', 'listings', a?.id, params.toString()],
    queryFn: () => api<{ total: number; pageSize: number; items: ListingRow[] }>(`/channels/shopee/accounts/${a!.id}/listings?${new URLSearchParams({
      link, page: String(page),
      ...Object.fromEntries(['q', 'status', 'dup', 'alert', 'department', 'category', 'subcategory', 'attrs'].filter((k) => params.get(k)).map((k) => [k, params.get(k)!])),
    })}`),
    enabled: !!a,
    placeholderData: keepPreviousData,
  });
  const me = useMe();
  const qc = useQueryClient();
  const canEdit = can(me.tenant.access, 'listings', 'write') && !me.impersonating;
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<ListingRow | null>(null);
  useEffect(() => setSelected(new Set()), [params.toString()]); // eslint-disable-line react-hooks/exhaustive-deps
  const rows = q.data?.items ?? [];
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggle = (id: string) => setSelected((cur) => { const n = new Set(cur); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const bulk = useMutation({
    mutationFn: (status: 'active' | 'paused') => api<{ ok: number; failed: { externalId: string; error: string }[] }>('/channels/shopee/listings/bulk-status', { method: 'POST', json: { ids: [...selected], status } }),
    onSuccess: () => { setSelected(new Set()); qc.invalidateQueries({ queryKey: ['shopee'] }); },
  });
  return (
    <>
      <Head title="Anúncios na Shopee"><p>O que está na loja, o produto da Berytus vinculado (pelo SKU ou EAN) e o preço/estoque que a Berytus envia.</p></Head>
      {ov.isPending ? <Loading /> : !a ? <NoShop /> : (
        <>
          <div className="toolbar">
            <label className="search">
              <Search size={16} /><span className="sr-only">Buscar</span>
              <input className="input" type="search" placeholder="Buscar por título, SKU ou nº do anúncio" value={search} onChange={(e) => setSearch(e.target.value)} />
            </label>
            <div className="seg" role="group" aria-label="Vínculo">
              {([['all', 'Todos'], ['linked', 'Vinculados'], ['unlinked', 'Sem vínculo']] as const).map(([v, l]) => (
                <button key={v} type="button" aria-pressed={link === v} onClick={() => set({ link: v === 'all' ? null : v, page: null })}>{l}</button>
              ))}
            </div>
            <ListingCategorySelect channel="shopee" accountId={a.id} params={params} set={set} />
            <AttrFilterButton params={params} set={set} />
            <select className="select" aria-label="Situação" value={params.get('status') ?? 'all'} onChange={(e) => set({ status: e.target.value === 'all' ? null : e.target.value, page: null })}>
              <option value="all">Qualquer situação</option>
              <option value="active">Ativos</option>
              <option value="paused">Pausados</option>
              <option value="other">Outras</option>
            </select>
            <select className="select" aria-label="Alertas" value={params.get('alert') ?? ''} onChange={(e) => set({ alert: e.target.value || null, page: null })}>
              <option value="">Qualquer alerta</option>
              <option value="any">Com algum alerta</option>
              <option value="none">Sem nenhum alerta</option>
              {(Object.keys(LISTING_ALERT) as ListingAlert[]).filter((k) => k !== 'buybox_lost').map((k) => <option key={k} value={k}>{LISTING_ALERT[k].label}</option>)}
            </select>
            <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" className="check" checked={params.get('dup') === 'any'} onChange={(e) => set({ dup: e.target.checked ? 'any' : null, page: null })} />
              Só SKUs com mais de 1 anúncio
            </label>
            {params.get('dup') && <DuplicatesButton accountId={a.id} />}
            <span className="grow" />
            <a className="btn" href={`/api/v1/exports/listings.csv?channel=shopee&account=${a.id}&status=active${categoryParams(params)}`}><Download size={16} /> Exportar ativos</a>
          </div>
          {selected.size > 0 && canEdit && (
            <div className="bulkbar" role="status" style={{ marginBottom: 12 }}>
              <b>{int(selected.size)} selecionado(s)</b>
              <button className="btn sm" type="button" disabled={bulk.isPending}
                onClick={() => confirm(`Pausar ${selected.size} anúncio(s) na Shopee? Eles saem do ar até você ativar de novo (com todas as variações).`) && bulk.mutate('paused')}>Pausar</button>
              <button className="btn sm" type="button" disabled={bulk.isPending} onClick={() => bulk.mutate('active')}>Ativar</button>
              {bulk.isPending && <span className="small">Enviando à Shopee…</span>}
              <span style={{ flex: 1 }} />
              <button className="btn sm quiet" type="button" onClick={() => setSelected(new Set())}><X size={14} /> Limpar</button>
            </div>
          )}
          {bulk.error && <ErrorBox error={bulk.error} />}
          {bulk.data && (
            <div className={`notice small${bulk.data.failed.length ? ' warn' : ''}`} style={{ marginBottom: 12 }}>
              {int(bulk.data.ok)} anúncio(s) alterado(s) na Shopee.
              {bulk.data.failed.length > 0 && <> {int(bulk.data.failed.length)} recusado(s): {bulk.data.failed.slice(0, 5).map((f) => `${f.externalId} (${f.error})`).join('; ')}{bulk.data.failed.length > 5 ? '…' : ''}</>}
            </div>
          )}
          {editing && <ShopeeListingEditor l={editing} shopId={a.shopId} onClose={() => setEditing(null)} />}
          <div className="panel flush">
            {q.error ? <div style={{ padding: 18 }}><ErrorBox error={q.error} /></div> : q.isPending ? <Loading /> : !q.data?.items.length ? (
              <div className="empty"><b>Nenhum anúncio</b>{a.listings.total === 0 ? 'Clique em “Ler anúncios agora” no resumo, ou publique produtos.' : 'Nada com esse filtro.'}</div>
            ) : (
              <div className="grid-wrap">
                <table className="simple">
                  <thead><tr>
                    {canEdit && <th style={{ width: 36 }}><input type="checkbox" className="check" aria-label="Selecionar a página" checked={allOnPage}
                      onChange={() => setSelected(allOnPage ? new Set() : new Set(rows.map((r) => r.id)))} /></th>}
                    <th>Anúncio</th><th>Situação</th><th className="num">Preço Shopee</th><th className="num">Estoque Shopee</th><th>Produto na Berytus</th><th className="num">Preço Berytus</th><th className="num">Estoque Berytus</th><th>Alertas</th><th />
                  </tr></thead>
                  <tbody>
                    {q.data.items.map((l) => {
                      const st = LISTING_STATUS[l.status] ?? { label: l.status, tone: 'off' };
                      const priceDiff = l.berytus.price != null && l.price != null && Math.abs(l.berytus.price - l.price) >= 0.01;
                      const stockDiff = l.berytus.stock != null && l.stock != null && l.berytus.stock !== l.stock;
                      return (
                        <tr key={l.id}>
                          {canEdit && <td><input type="checkbox" className="check" aria-label={`Selecionar ${l.title}`} checked={selected.has(l.id)} onChange={() => toggle(l.id)} /></td>}
                          <td>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                              <Thumb src={l.thumbnail} alt="" />
                              <div>
                                <div><a className="ext" href={shopeeUrl(a.shopId, l.itemId)} target="_blank" rel="noreferrer" title="Abrir o anúncio na Shopee">{l.title} <ExternalLink size={11} /></a></div>
                                <div className="small muted">nº {l.itemId}{l.modelId ? ` · variação ${l.modelId}` : ''}{l.sellerSku ? ` · SKU ${l.sellerSku}` : ''}{l.ean ? ` · EAN ${l.ean}` : ''}</div>
                              </div>
                            </div>
                          </td>
                          <td><Tag tone={st.tone}>{st.label}</Tag></td>
                          <td className="num">{l.price != null ? brl(l.price) : '—'}</td>
                          <td className="num">{l.stock != null ? int(l.stock) : '—'}</td>
                          <td>
                            {l.product ? <><Link to={`/catalogo/${l.product.id}`}>{l.product.title ?? l.product.sku}</Link><div className="small muted">SKU {l.product.sku}</div></>
                              : <span className="small" style={{ color: 'var(--warn)' }}>sem vínculo: o SKU ou EAN do anúncio não bate com um produto</span>}
                            {l.berytus.blocked && <div className="small muted">bloqueado: {l.berytus.blocked}</div>}
                            {l.berytus.error && <div className="small" style={{ color: 'var(--err)' }}>{l.berytus.error}</div>}
                          </td>
                          <td className="num" style={priceDiff ? { color: 'var(--warn)' } : undefined}>{l.berytus.price != null ? brl(l.berytus.price) : '—'}</td>
                          <td className="num" style={stockDiff ? { color: 'var(--warn)' } : undefined}>{l.berytus.stock != null ? int(l.berytus.stock) : '—'}</td>
                          <td>
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', maxWidth: 200 }}>
                              {l.alerts.map((x) => (
                                <span key={x} title={TITLE_ALERTS.has(x) ? l.titleAlertDetail ?? undefined : undefined}><Tag tone={LISTING_ALERT[x].tone}>{LISTING_ALERT[x].label}</Tag></span>
                              ))}
                            </div>
                          </td>
                          <td>{canEdit && <button className="btn sm" type="button" onClick={() => setEditing(l)}>Editar</button>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {q.data && <Pager total={q.data.total} page={page} pageSize={q.data.pageSize} onPage={(n) => set({ page: n > 1 ? String(n) : null })} />}
          </div>
          <p className="small muted">Em laranja: diferente do que está na Shopee (vai no próximo envio, se o envio estiver ligado).</p>
        </>
      )}
    </>
  );
}


const moneyIn = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const moneyOut = (v: string) => Number(v.replace(/\./g, '').replace(',', '.'));

/** Editar um anúncio da Shopee: preço da linha (anúncio ou variação) e pausar/ativar (o anúncio inteiro). Vai na hora para a Shopee. */
function ShopeeListingEditor({ l, shopId, onClose }: { l: ListingRow; shopId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [price, setPrice] = useState(l.price != null ? moneyIn(l.price) : '');
  const [status, setStatus] = useState(l.status);
  const save = useMutation({
    mutationFn: (body: { price?: number; status?: 'active' | 'paused' }) => api<{ updated: string[] }>(`/channels/shopee/listings/${l.id}`, { method: 'PATCH', json: body }),
    onSuccess: (_r, body) => { if (body.status) setStatus(body.status); qc.invalidateQueries({ queryKey: ['shopee'] }); },
  });
  const value = moneyOut(price);
  const priceOk = Number.isFinite(value) && value > 0;
  const dirty = priceOk && value !== l.price;
  const locked = !['active', 'paused'].includes(status);
  const st = LISTING_STATUS[status] ?? { label: status, tone: 'off' };
  return (
    <Modal title="Editar anúncio na Shopee" onClose={onClose}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <Thumb src={l.thumbnail} alt="" />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div>{l.title}</div>
          <div className="small muted">
            <a className="ext" href={shopeeUrl(shopId, l.itemId)} target="_blank" rel="noreferrer">nº {l.itemId} <ExternalLink size={11} /></a>
            {l.modelId ? ` · variação ${l.modelId}` : ''}{l.sellerSku ? ` · SKU ${l.sellerSku}` : ''}
          </div>
          <div style={{ marginTop: 4 }}><Tag tone={st.tone}>{st.label}</Tag></div>
        </div>
        {!locked && (status === 'active'
          ? <button className="btn sm" type="button" disabled={save.isPending}
              onClick={() => confirm('Pausar o anúncio na Shopee? Ele sai do ar (com todas as variações) até você ativar de novo.') && save.mutate({ status: 'paused' })}>Pausar</button>
          : <button className="btn sm primary" type="button" disabled={save.isPending} onClick={() => save.mutate({ status: 'active' })}>Ativar</button>)}
      </div>
      {locked && <div className="notice small">Anúncio banido, em revisão ou excluído na Shopee: a Berytus não altera. Veja o motivo no Seller Center.</div>}
      <fieldset disabled={locked || save.isPending} style={{ border: 0, padding: 0, margin: 0, display: 'grid', gap: 8 }}>
        <div className="field">
          <label htmlFor="sh-price">Preço na Shopee (R$){l.modelId ? ', desta variação' : ''}</label>
          <input className="input" id="sh-price" inputMode="decimal" style={{ maxWidth: 180 }} value={price} onChange={(e) => setPrice(e.target.value)} />
          {l.berytus.price != null && <span className="small muted">Preço da Berytus para este produto: {brl(l.berytus.price)}</span>}
        </div>
      </fieldset>
      <p className="small muted" style={{ margin: 0 }}>
        As alterações vão na hora para a Shopee. A tabela de preços da Berytus não muda: com o envio de preço ligado, o próximo envio volta para o preço da Berytus.
      </p>
      {save.error && <ErrorBox error={save.error} />}
      {save.isSuccess && !dirty && <div className="notice small">Alterado na Shopee{save.data?.updated.length ? '' : ' (nada mudou)'}.</div>}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button className="btn primary" type="button" disabled={!dirty || locked || save.isPending} onClick={() => save.mutate({ price: value })}>
          {save.isPending ? 'Enviando…' : 'Salvar preço na Shopee'}
        </button>
        <button className="btn" type="button" onClick={onClose}>Fechar</button>
        {dirty && <span className="small muted">Novo preço: {brl(value)}</span>}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------------------------
// Publicar
// ---------------------------------------------------------------------------------------------

type ItemStatus = 'nao_publicado' | 'pronto' | 'pendente' | 'publicando' | 'publicado' | 'erro';
interface Item {
  productId: string; sku: string; title: string; brand: string | null; group: string | null; photo: string | null;
  ean: string | null; gtinStatus: 'valid' | 'corrected' | 'invalid' | 'none'; eanReceived: string | null;
  price: number | null; stock: number | null;
  status: ItemStatus; reasons: { code: string; label: string }[]; missing: string[]; error: string | null;
  category: { id: number; path: string | null; origin: string | null; originLabel: string | null } | null;
  categoryError: string | null; alternatives: number; itemId: string | null; updatedAt: string | null;
}
interface ResolveJob {
  id: string; status: string; error: string | null; createdAt: string;
  stats: { total?: number; done?: number; pronto?: number; pendente?: number; erro?: number; publicado?: number; motivos?: Record<string, number>; origens?: Record<string, number> } | null;
}
interface ItemsPage {
  total: number; page: number; pageSize: number; counts: Record<ItemStatus | 'all' | 'sem_anuncio', number>;
  reasons: { code: string; label: string; count: number }[];
  groups: { key: string; label: string; count: number }[];
  classes: { department: string | null; category: string | null; count: number }[];
  base: { categories: number; fetchedAt: string | null };
  baseJob: ResolveJob | null;
  job: ResolveJob | null;
  items: Item[];
}
const STATUS: Record<ItemStatus, { label: string; one: string; tone: string }> = {
  nao_publicado: { label: 'Não avaliados', one: 'Não avaliado', tone: 'off' },
  pronto: { label: 'Prontos', one: 'Pronto', tone: 'ok' },
  pendente: { label: 'Pendentes', one: 'Pendente', tone: 'warn' },
  publicando: { label: 'Publicando', one: 'Publicando', tone: 'info' },
  publicado: { label: 'Publicados', one: 'Publicado', tone: 'ok' },
  erro: { label: 'Erros', one: 'Erro', tone: 'err' },
};
// "Sem anúncio": todos os que ainda não têm anúncio na loja (o produto não some da aba quando a categoria é resolvida).
type Tab = ItemStatus | 'all' | 'sem_anuncio';
const STATUS_ORDER: Tab[] = ['all', 'sem_anuncio', 'nao_publicado', 'pronto', 'pendente', 'publicando', 'publicado', 'erro'];
const tabLabel = (k: Tab) => (k === 'all' ? 'Todos' : k === 'sem_anuncio' ? 'Sem anúncio' : STATUS[k].label);
const ORIGIN: Record<string, string> = { mestre: 'catálogo mestre', de_para: 'de-para', shopee_recommend: 'sugerida pela Shopee', manual: 'escolhida à mão' };
/** Endereço do anúncio na Shopee (loja/anúncio). */
const shopeeUrl = (shopId: string, itemId: string) => `https://shopee.com.br/product/${shopId}/${itemId}`;
type Selection = { productIds: string[] } | { filter: Record<string, string> } | { list: string[] };

/**
 * Publicar na Shopee: a situação de cada produto na loja, paginada (a tela só lê; nada é calculado ao abrir).
 * Escolha os produtos (marcados, todos do filtro ou uma lista colada) e peça "Resolver categoria": o worker resolve
 * pelo catálogo mestre, pelo de-para da sua categoria ou pela sugestão da Shopee e grava pronto / pendente (com o motivo).
 */
export function ShopeePublish() {
  const { q: ov, account: a } = useShopee();
  const admin = can(useMe().tenant.access, 'publications', 'write');
  const qc = useQueryClient();
  const [params, set] = useUrlState();
  const [search, setSearch] = useSearchBox(params, set);
  const status = (params.get('situacao') as Tab | null) ?? 'all';
  const department = params.get('departamento') ?? '';
  const productCategory = params.get('cat') ?? '';
  const reason = params.get('motivo') ?? '';
  const reasons = reason ? reason.split(',') : [];
  const reasonMode = (params.get('modo') as 'any' | 'only' | 'not' | null) ?? 'any';
  const toggleReason = (code: string) => {
    const next = reasons.includes(code) ? reasons.filter((x) => x !== code) : [...reasons, code];
    set({ motivo: next.length ? next.join(',') : null, page: null }); setSelected(new Set());
  };
  const group = params.get('grupo');
  const brand = params.get('marca') ?? '';
  const page = Number(params.get('page') ?? 1);
  const [brandInput, setBrandInput] = useState(brand);
  useEffect(() => { const t = setTimeout(() => { if (brandInput.trim() !== brand) set({ marca: brandInput.trim() || null, page: null }); }, 400); return () => clearTimeout(t); }, [brandInput]); // eslint-disable-line react-hooks/exhaustive-deps
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [adjust, setAdjust] = useState<string | null>(null);
  const [swap, setSwap] = useState<Item | null>(null);
  const [bulk, setBulk] = useState<'category' | 'attr' | null>(null);
  const [pasting, setPasting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [dups, setDups] = useState<DupSkip[]>([]);
  const filter: Record<string, string> = {
    ...(status !== 'all' ? { status } : {}), ...(reason ? { reason, ...(reasonMode !== 'any' ? { reasonMode } : {}) } : {}), ...(group != null ? { group } : {}),
    ...(brand ? { brand } : {}), ...(params.get('q') ? { q: params.get('q')! } : {}),
    ...(department ? { department } : {}), ...(productCategory ? { productCategory } : {}),
    ...(params.get('estoque') === 'in' || params.get('estoque') === 'out' ? { stock: params.get('estoque')! } : {}),
    ...(params.get('attrs') ? { attrs: params.get('attrs')! } : {}),
  };
  const qs = new URLSearchParams({ ...filter, page: String(page) });
  const list = useQuery({
    queryKey: ['shopee', 'items', a?.id, qs.toString()],
    queryFn: () => api<ItemsPage>(`/channels/shopee/accounts/${a!.id}/items?${qs}`),
    enabled: !!a,
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const x = query.state.data;
      if (!x) return false;
      const busy = ['queued', 'running'].includes(x.job?.status ?? '') || ['queued', 'running'].includes(x.baseJob?.status ?? '');
      return busy || x.counts.publicando > 0 ? 4000 : false;
    },
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['shopee'] });
  const readBase = useMutation({ mutationFn: () => api(`/channels/shopee/accounts/${a!.id}/base/refresh`, { method: 'POST', json: { force: false } }), onSuccess: refresh });
  const resolve = useMutation({
    mutationFn: (v: Selection & { force?: boolean }) => api<{ total: number; notFound: string[] }>(`/channels/shopee/accounts/${a!.id}/items/resolve`, { method: 'POST', json: v }),
    onSuccess: (r) => {
      setSelected(new Set());
      setPasting(false);
      setNotice(`Resolvendo a categoria de ${int(r.total)} produto(s) em segundo plano.${r.notFound.length ? ` Não encontrados: ${r.notFound.slice(0, 20).join(', ')}${r.notFound.length > 20 ? '…' : ''}` : ''}`);
      refresh();
    },
  });
  const reevaluate = useMutation({
    mutationFn: (v: Selection) => api<{ total: number; pronto: number; pendente: number; motivos: { code: string; label: string; n: number }[] }>(
      `/channels/shopee/accounts/${a!.id}/items/reevaluate`, { method: 'POST', json: { ...v, refreshSpecs: true } }),
    onSuccess: (r) => {
      setSelected(new Set());
      setNotice(`Reavaliados ${int(r.total)} produto(s): ${int(r.pronto)} pronto(s), ${int(r.pendente)} pendente(s)`
        + `${r.motivos.length ? ` (${r.motivos.slice(0, 5).map((m) => `${m.label.toLowerCase()}: ${int(m.n)}`).join(' · ')})` : ''}.`
        + ' A Berytus também relê na Shopee os atributos obrigatórios dessas categorias e recalcula de novo em alguns minutos.');
      refresh();
      setTimeout(refresh, 60_000);
    },
  });
  const publish = useMutation({
    mutationFn: (v: Selection) => api<{ queued: number; skipped: DupSkip[]; notReady: number }>(`/channels/shopee/accounts/${a!.id}/items/publish`, { method: 'POST', json: v }),
    onSuccess: (r) => {
      setDups(r.skipped.filter((x) => x.reason === 'listed'));
      setSelected(new Set());
      const other = r.skipped.filter((x) => x.reason !== 'listed');
      setNotice(r.queued
        ? `${int(r.queued)} produto(s) na fila de publicação.${r.notReady ? ` ${int(r.notReady)} não estavam prontos e ficaram de fora.` : ''}${other.length ? ` ${int(other.length)} barrado(s): ${other.slice(0, 5).map((x) => `${x.sku} (${x.label})`).join(', ')}.` : ''}`
        : 'Nenhum produto pronto nessa seleção: resolva a categoria antes.');
      refresh();
    },
  });
  const d = list.data;
  const items = d?.items ?? [];
  const job = d?.job;
  const running = !!job && ['queued', 'running'].includes(job.status);
  const allOnPage = items.length > 0 && items.every((i) => selected.has(i.productId));
  const toggle = (id: string) => setSelected((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const pick = (k: string, v: string | null) => { set({ [k]: v, page: null }); setSelected(new Set()); };
  const filtered = status === 'all' ? d?.counts.all ?? 0 : d?.counts[status] ?? 0;

  return (
    <>
      <Head title="Publicar na Shopee">
        <p>
          A situação de cada produto nesta loja. Escolha os produtos (marcados, todos do filtro ou uma lista de SKU/EAN) e peça <b>Resolver categoria</b>:
          a Berytus usa a categoria do catálogo mestre, o de-para da sua categoria ou a sugestão da Shopee, e marca cada produto como pronto ou pendente (com o motivo).
          Depois, publique os prontos. Medicamentos: a Shopee só aceita MIP; controlados nunca vão.
        </p>
      </Head>
      {ov.isPending ? <Loading /> : !a ? <NoShop /> : (
        <div className="stack-y">
          <TestNotice a={a} />
          {list.error && <ErrorBox error={list.error} />}
          {list.isPending && <Loading />}
          {d && (
            <>
              <BaseStatus base={d.base} job={d.baseJob} onRetry={() => readBase.mutate()} busy={readBase.isPending} />
              <ResolveSummary job={job} onReason={(r) => { set({ situacao: 'pendente', motivo: r, modo: null, page: null }); setSelected(new Set()); }} />
              <div className="seg" role="tablist" aria-label="Situação" style={{ flexWrap: 'wrap' }}>
                {STATUS_ORDER.map((k) => (
                  <button key={k} type="button" aria-pressed={status === k} onClick={() => { set({ situacao: k === 'all' ? null : k, motivo: null, modo: null, page: null }); setSelected(new Set()); }}>
                    {tabLabel(k)} <span className="muted">{int(d.counts[k] ?? 0)}</span>
                  </button>
                ))}
              </div>
              {status === 'pendente' && d.reasons.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <div className="seg" role="group" aria-label="Motivo" style={{ flexWrap: 'wrap' }}>
                    <button type="button" aria-pressed={!reasons.length} onClick={() => { set({ motivo: null, modo: null, page: null }); setSelected(new Set()); }}>Todos os motivos</button>
                    {d.reasons.map((r) => (
                      <button key={r.code} type="button" aria-pressed={reasons.includes(r.code)} onClick={() => toggleReason(r.code)}>{r.label} <span className="muted">{int(r.count)}</span></button>
                    ))}
                  </div>
                  {reasons.length > 0 && (
                    <select className="select" aria-label="Como filtrar os motivos" value={reasonMode}
                      onChange={(e) => { set({ modo: e.target.value === 'any' ? null : e.target.value, page: null }); setSelected(new Set()); }}>
                      <option value="any">Com algum dos marcados</option>
                      <option value="only">Só com os marcados (nenhum outro motivo)</option>
                      <option value="not">Sem os marcados</option>
                    </select>
                  )}
                </div>
              )}

              <div className="toolbar" style={{ flexWrap: 'wrap' }}>
                <label className="search">
                  <Search size={16} /><span className="sr-only">Buscar</span>
                  <input className="input" type="search" placeholder="Buscar por nome, SKU ou EAN" value={search} onChange={(e) => setSearch(e.target.value)} />
                </label>
                <ClassSelect classes={d.classes} department={department} category={productCategory}
                  onChange={(dep, cat) => { set({ departamento: dep || null, cat: cat || null, grupo: null, page: null }); setSelected(new Set()); }} />
                {group != null && <button type="button" className="btn sm" onClick={() => pick('grupo', null)}>Grupo: {group || 'sem categoria'} ✕</button>}
                <select className="select" aria-label="Estoque" value={params.get('estoque') ?? ''} onChange={(e) => pick('estoque', e.target.value || null)}>
                  <option value="">Estoque: todos</option>
                  <option value="in">Com estoque</option>
                  <option value="out">Sem estoque</option>
                </select>
                <AttrFilterButton params={params} set={(p) => { set(p); setSelected(new Set()); }} />
                <input className="input" aria-label="Marca" placeholder="Marca" value={brandInput} style={{ maxWidth: 160 }} onChange={(e) => setBrandInput(e.target.value)} />
                <span className="grow" />
                {admin && (
                  <>
                    <button className="btn" type="button" onClick={() => setPasting(true)}>Colar lista</button>
                    <button className="btn" type="button" disabled={!filtered || running || resolve.isPending || filtered > 5000}
                      title={filtered > 5000 ? 'No máximo 5.000 por vez: use os filtros' : 'Resolve a categoria de todos os produtos do filtro'}
                      onClick={() => resolve.mutate({ filter })}>
                      <Sparkles size={15} /> Resolver categoria ({int(filtered)})
                    </button>
                    <button className="btn" type="button" disabled={!filtered || reevaluate.isPending || filtered > 5000}
                      title="Sem trocar a categoria: relê na Shopee os atributos obrigatórios e recalcula pronto/pendente e os motivos (use depois de mudar a configuração)"
                      onClick={() => reevaluate.mutate({ filter })}>Reavaliar ({int(filtered)})</button>
                    <button className="btn primary" type="button" disabled={!d.counts.pronto || publish.isPending}
                      onClick={() => publish.mutate({ filter: { ...filter, status: 'pronto' } })}>
                      <Send size={15} /> Publicar prontos{status === 'all' || status === 'pronto' ? ` (${int(d.counts.pronto)})` : ''}
                    </button>
                  </>
                )}
              </div>
              {running && (
                <div className="notice small">
                  Resolvendo categorias em segundo plano… {int(job?.stats?.done ?? 0)} de {int(job?.stats?.total ?? 0)} produto(s).
                </div>
              )}
              {(resolve.error || publish.error) && <ErrorBox error={resolve.error ?? publish.error} />}
              {notice && <div className="notice small">{notice} <Link to="/canal/shopee/publicacoes">Ver publicações</Link></div>}
              <DupNotice accountId={a.id} dups={dups} onDone={(n) => { setDups([]); setNotice(`${int(n)} produto(s) na fila de publicação (mesmo já tendo anúncio).`); }} />

              {selected.size > 0 && admin && (
                <div className="notice" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <b>{int(selected.size)} selecionado(s)</b>
                  <button className="btn sm" type="button" disabled={running || resolve.isPending} onClick={() => resolve.mutate({ productIds: [...selected] })}><Sparkles size={13} /> Resolver categoria</button>
                  <button className="btn sm" type="button" onClick={() => setBulk('category')}>Definir categoria</button>
                  <button className="btn sm" type="button" onClick={() => setBulk('attr')}>Preencher atributo</button>
                  <button className="btn sm primary" type="button" disabled={publish.isPending} onClick={() => publish.mutate({ productIds: [...selected] })}><Send size={13} /> Publicar os prontos</button>
                  <button className="btn sm quiet" type="button" onClick={() => setSelected(new Set())}>Limpar</button>
                </div>
              )}

              <div className="panel flush" style={{ opacity: list.isPlaceholderData ? 0.6 : 1 }}>
                {!items.length ? (
                  <div className="empty"><b>Nada por aqui</b>Nenhum produto com esse filtro.</div>
                ) : (
                  <div className="grid-wrap">
                    <table className="simple">
                      <thead>
                        <tr>
                          <th style={{ width: 32 }}><input type="checkbox" className="check" aria-label="Selecionar a página" checked={allOnPage}
                            onChange={() => setSelected((s) => { const n = new Set(s); for (const i of items) { if (allOnPage) n.delete(i.productId); else n.add(i.productId); } return n; })} /></th>
                          <th>Produto</th><th>Categoria na Shopee</th><th>Situação</th><th className="num">Preço</th><th className="num">Estoque</th><th />
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((i) => (
                          <tr key={i.productId}>
                            <td><input type="checkbox" className="check" aria-label={`Selecionar ${i.sku}`} checked={selected.has(i.productId)} onChange={() => toggle(i.productId)} /></td>
                            <td>
                              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                <Thumb src={i.photo} alt="" />
                                <div style={{ minWidth: 0 }}>
                                  <Link to={`/catalogo/${i.productId}`} style={{ display: 'block', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={i.title}>{i.title}</Link>
                                  <div className="small muted">
                                    SKU {i.sku}{i.brand ? ` · ${i.brand}` : ''}{i.ean ? ` · EAN ${i.ean}` : ''}
                                    {i.gtinStatus === 'corrected' && <span title={`Recebido ${i.eanReceived}: a Berytus completou os zeros à esquerda`}> · corrigido</span>}
                                  </div>
                                  {i.gtinStatus === 'invalid' && <div className="small" style={{ color: 'var(--err)' }}>GTIN inválido</div>}
                                  <div className="small muted" style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={i.group ?? ''}>{i.group ?? 'Sem categoria no cadastro'}</div>
                                </div>
                              </div>
                            </td>
                            <td style={{ maxWidth: 300 }}>
                              {i.category ? (
                                <>
                                  <div className="small" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={i.category.path ?? ''}>{i.category.path ?? <span className="muted">nº {i.category.id} (caminho em atualização)</span>}</div>
                                  <div className="small muted">{ORIGIN[i.category.origin ?? ''] ?? '—'}{i.alternatives ? ` · ${int(i.alternatives)} alternativa(s)` : ''}</div>
                                </>
                              ) : <span className="small muted">{i.categoryError ?? (i.status === 'publicado' ? '—' : 'ainda não resolvida')}</span>}
                              {admin && i.status !== 'publicado' && i.status !== 'publicando' && (
                                <button type="button" className="btn sm quiet" style={{ padding: '0 4px' }} onClick={() => setSwap(i)}>Trocar</button>
                              )}
                            </td>
                            <td style={{ maxWidth: 280 }}>
                              <Tag tone={STATUS[i.status].tone}>{STATUS[i.status].one}</Tag>
                              {i.reasons.length > 0 && <div className="small" style={{ color: 'var(--warn)', marginTop: 3, whiteSpace: 'normal' }}>{i.reasons.map((r) => r.label).join(' · ')}</div>}
                              {i.missing.length > 0 && <div className="small muted" style={{ whiteSpace: 'normal' }}>Faltam: {i.missing.join(', ')}</div>}
                              {i.error && <div className="small" style={{ color: 'var(--err)', marginTop: 3, whiteSpace: 'normal' }}>{i.error}</div>}
                              {i.itemId && <div className="small"><a href={shopeeUrl(a.shopId, i.itemId)} target="_blank" rel="noreferrer">nº {i.itemId}</a></div>}
                            </td>
                            <td className="num">{i.price != null ? brl(i.price) : <span className="muted">—</span>}</td>
                            <td className="num">{i.stock != null ? int(i.stock) : '—'}</td>
                            <td>{admin && i.status !== 'publicado' && i.status !== 'publicando' && <button type="button" className="btn sm" onClick={() => setAdjust(i.productId)}><Wrench size={13} /> Ajustar</button>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <Pager total={d.total} page={page} pageSize={d.pageSize} onPage={(n) => set({ page: n > 1 ? String(n) : null })} />
              </div>
              <DeParaList accountId={a.id} admin={admin} groups={d.groups} />
            </>
          )}
          {/* key: cada produto abre um modal novo (nada do item anterior fica no estado). */}
          <ShopeeAdjust key={adjust ?? 'fechado'} accountId={a.id} productId={adjust} onClose={() => { setAdjust(null); refresh(); }} />
          {swap && <SwapCategory accountId={a.id} item={swap} admin={admin} onClose={(done) => { setSwap(null); if (done) refresh(); }} />}
          {pasting && (
            <PasteList busy={resolve.isPending} error={resolve.error} onClose={() => setPasting(false)} onSubmit={(codes) => resolve.mutate({ list: codes })} />
          )}
          {bulk === 'category' && <BulkCategory accountId={a.id} productIds={[...selected]} onClose={(done) => { setBulk(null); if (done) refresh(); }} />}
          {bulk === 'attr' && <BulkAttribute accountId={a.id} productIds={[...selected]} onClose={(done) => { setBulk(null); if (done) refresh(); }} />}
        </div>
      )}
    </>
  );
}

const REASON_LABEL: Record<string, string> = {
  sem_categoria: 'sem categoria', categoria_recusada: 'categoria recusada', sem_especificacoes: 'atributos da categoria não lidos', sem_marca: 'marca fora da lista',
  atributo_obrigatorio: 'atributo obrigatório', falta_codigo_anvisa: 'falta código Anvisa (medicamento)', anvisa_nao_medicamento: 'Shopee pede registro Anvisa (não medicamento)', falha_consulta_shopee: 'falha ao consultar a Shopee',
  gtin_invalido: 'GTIN inválido', sem_gtin: 'sem GTIN', medicamento_prescricao: 'medicamento com prescrição', medicamento_controlado: 'controlado',
  tarja_nao_informada: 'tarja não informada', medicamento_bloqueado: 'medicamento não MIP',
  sem_foto: 'sem foto', sem_preco: 'sem preço', sem_estoque: 'sem estoque', inativo: 'inativo',
};

/** Resumo da última resolução: X prontos, Y pendentes por motivo, Z erros. */
function ResolveSummary({ job, onReason }: { job: ResolveJob | null | undefined; onReason: (r: string) => void }) {
  if (!job || ['queued', 'running'].includes(job.status)) return null;
  if (job.status === 'failed') return <div className="notice warn small">A última resolução de categorias falhou: {job.error ?? 'erro desconhecido'}. Peça de novo.</div>;
  const s = job.stats ?? {};
  const motivos = Object.entries(s.motivos ?? {}).sort((x, y) => y[1] - x[1]);
  const origens = Object.entries(s.origens ?? {}).filter(([, n]) => n > 0);
  return (
    <div className="notice small" style={{ display: 'grid', gap: 4 }}>
      <span>
        <b>Última resolução</b> ({dateTime(job.createdAt)}): {int(s.total ?? 0)} produto(s) — <b style={{ color: 'var(--ok)' }}>{int(s.pronto ?? 0)} pronto(s)</b>,{' '}
        <b>{int(s.pendente ?? 0)} pendente(s)</b>, <b style={{ color: s.erro ? 'var(--err)' : undefined }}>{int(s.erro ?? 0)} erro(s)</b>{s.publicado ? `, ${int(s.publicado)} já publicado(s)` : ''}.
      </span>
      {motivos.length > 0 && (
        <span>Pendentes por motivo: {motivos.map(([m, n], k) => (
          <span key={m}>{k > 0 && ' · '}<button type="button" className="btn sm quiet" style={{ padding: '0 2px' }} onClick={() => onReason(m)}>{REASON_LABEL[m] ?? m} {int(n)}</button></span>
        ))}</span>
      )}
      {origens.length > 0 && <span className="muted">Categoria por: {origens.map(([o, n]) => `${ORIGIN[o] ?? o} ${int(n)}`).join(' · ')}</span>}
    </div>
  );
}

/** Lista colada (SKU ou EAN, um por linha ou separados por vírgula): resolve a categoria desses produtos. */
function PasteList({ onSubmit, onClose, busy, error }: { onSubmit: (codes: string[]) => void; onClose: () => void; busy: boolean; error: unknown }) {
  const [text, setText] = useState('');
  const codes = [...new Set(text.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean))];
  return (
    <Modal title="Resolver categoria de uma lista" onClose={onClose}>
      <span className="small muted">Cole os SKUs ou EANs (um por linha, ou separados por vírgula). Até 5.000.</span>
      <textarea className="input" rows={8} value={text} onChange={(e) => setText(e.target.value)} placeholder={'1322184\n7891058017446'} />
      {error ? <ErrorBox error={error} /> : null}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn" type="button" onClick={onClose}>Cancelar</button>
        <button className="btn primary" type="button" disabled={!codes.length || codes.length > 5000 || busy} onClick={() => onSubmit(codes)}>Resolver {int(codes.length)}</button>
      </div>
    </Modal>
  );
}

/** "Trocar" a categoria de um produto: as alternativas da Shopee ou a busca; pode valer como de-para da categoria interna. */
function SwapCategory({ accountId, item, admin, onClose }: { accountId: string; item: Item; admin: boolean; onClose: (done: boolean) => void }) {
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  const [toGroup, setToGroup] = useState(false);
  useEffect(() => { const t = setTimeout(() => setTerm(q.trim()), 250); return () => clearTimeout(t); }, [q]);
  const draft = useQuery({
    queryKey: ['shopee', 'draft-alts', accountId, item.productId],
    queryFn: () => api<{ suggestions: { id: number; path: string }[] }>(`/channels/shopee/accounts/${accountId}/products/${item.productId}/draft`),
  });
  const cats = useQuery({
    queryKey: ['shopee', 'cats', accountId, term],
    queryFn: () => api<{ items: { id: number; path: string }[]; loading?: boolean }>(`/channels/shopee/accounts/${accountId}/categories?q=${encodeURIComponent(term)}`),
    enabled: term.length >= 2,
  });
  const save = useMutation({
    mutationFn: (categoryId: number) => api(`/channels/shopee/accounts/${accountId}/items/${item.productId}/category`, { method: 'PUT', json: { categoryId, applyToGroup: toGroup } }),
    onSuccess: () => onClose(true),
  });
  const alts = (draft.data?.suggestions ?? []).filter((s) => s.id !== item.category?.id);
  return (
    <Modal title={`Categoria da Shopee para "${item.title}"`} onClose={() => onClose(false)}>
      {item.category && <span className="small muted">Atual: {item.category.path ?? item.category.id} ({ORIGIN[item.category.origin ?? ''] ?? '—'})</span>}
      {alts.length > 0 && (
        <div className="stack-y" style={{ gap: 6 }}>
          <span className="small muted">Outras sugestões da Shopee:</span>
          <PickList items={alts} keyOf={(s) => s.id} empty="" label={(s) => <>{s.path} <span className="muted small">· {s.id}</span></>} onPick={(s) => !save.isPending && save.mutate(s.id)} />
        </div>
      )}
      <label className="search" style={{ width: '100%' }}>
        <Search size={16} />
        <input className="input" autoFocus placeholder="Procurar outra categoria (ex.: analgésico, shampoo, fralda)" value={q} onChange={(e) => setQ(e.target.value)} />
      </label>
      {term.length >= 2 && (cats.isFetching && !cats.data ? <Loading /> : cats.error ? <ErrorBox error={cats.error} /> : (
        <PickList items={cats.data?.items ?? []} keyOf={(c) => c.id} empty={cats.data?.loading ? 'A base de categorias da Shopee está sendo lida; tente de novo em instantes.' : 'Nenhuma categoria com esse nome.'}
          label={(c) => <>{c.path} <span className="muted small">· {c.id}</span></>} onPick={(c) => !save.isPending && save.mutate(c.id)} />
      ))}
      {admin && item.group && (
        <label className="small" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="checkbox" className="check" checked={toGroup} onChange={(e) => setToGroup(e.target.checked)} />
          Usar também para a minha categoria "{item.group}" (de-para: vale nas próximas resoluções)
        </label>
      )}
      <span className="small muted">A troca fica como escolhida à mão e vira sugestão para o catálogo mestre da Berytus (entra no mestre só depois da conferência).</span>
      {save.error && <ErrorBox error={save.error} />}
    </Modal>
  );
}

/** De-para (minha categoria → Shopee): a regra fixa do MIP, os gravados, adicionar e tirar. */
function DeParaList({ accountId, admin, groups }: { accountId: string; admin: boolean; groups: { key: string; label: string; count: number }[] }) {
  const [open, setOpen] = useState(false);
  const [group, setGroup] = useState('');
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  useEffect(() => { const t = setTimeout(() => setTerm(q.trim()), 250); return () => clearTimeout(t); }, [q]);
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['shopee', 'de-para', accountId],
    queryFn: () => api<{ rules: { label: string; categoryId: number | null; path: string; found: boolean }[]; items: { key: string; label: string; categoryId: number; path: string | null; updatedAt: string }[] }>(`/channels/shopee/accounts/${accountId}/de-para`),
    enabled: open,
  });
  const cats = useQuery({
    queryKey: ['shopee', 'cats', accountId, term],
    queryFn: () => api<{ items: { id: number; path: string }[]; loading?: boolean }>(`/channels/shopee/accounts/${accountId}/categories?q=${encodeURIComponent(term)}`),
    enabled: open && !!group && term.length >= 2,
  });
  const done = () => qc.invalidateQueries({ queryKey: ['shopee', 'de-para', accountId] });
  const del = useMutation({ mutationFn: (key: string) => api(`/channels/shopee/accounts/${accountId}/de-para?key=${encodeURIComponent(key)}`, { method: 'DELETE' }), onSuccess: done });
  const add = useMutation({
    mutationFn: (categoryId: number) => api(`/channels/shopee/accounts/${accountId}/de-para`, { method: 'PUT', json: { group, categoryId } }),
    onSuccess: () => { setGroup(''); setQ(''); done(); },
  });
  return (
    <details className="panel" open={open} onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}>
      <summary className="small"><b>De-para de categorias</b> <span className="muted">(minha categoria → Shopee; usado quando o catálogo mestre não tem a categoria)</span></summary>
      {!open ? null : list.isPending ? <Loading /> : list.error ? <ErrorBox error={list.error} /> : (
        <div className="stack-y" style={{ gap: 10, marginTop: 8 }}>
          <table className="simple">
            <thead><tr><th>Minha categoria</th><th>Categoria na Shopee</th><th /></tr></thead>
            <tbody>
              {list.data!.rules.map((r) => (
                <tr key={r.label}>
                  <td className="small"><b>{r.label}</b></td>
                  <td className="small">{r.path}{!r.found && <span style={{ color: 'var(--warn)' }}> — não encontrada na árvore da Shopee (relida todo dia)</span>}</td>
                  <td className="small muted">fixa</td>
                </tr>
              ))}
              {list.data!.items.map((r) => (
                <tr key={r.key}>
                  <td className="small">{r.label}</td>
                  <td className="small">{r.path ?? `nº ${r.categoryId} (caminho em atualização)`}</td>
                  <td>{admin && <button className="btn sm quiet" type="button" disabled={del.isPending} onClick={() => del.mutate(r.key)}>Tirar</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {admin && (
            <div className="stack-y" style={{ gap: 6 }}>
              <span className="small muted">Adicionar: escolha a sua categoria e procure a categoria da Shopee. Vale nas próximas resoluções (medicamento nunca usa a sugestão da Shopee).</span>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <select className="select" aria-label="Minha categoria" value={group} style={{ maxWidth: 360 }} onChange={(e) => setGroup(e.target.value)}>
                  <option value="">Minha categoria…</option>
                  {groups.filter((g) => g.key).map((g) => <option key={g.key} value={g.key}>{g.label} ({int(g.count)})</option>)}
                </select>
                {group && (
                  <label className="search" style={{ flex: 1, minWidth: 220 }}>
                    <Search size={16} />
                    <input className="input" placeholder="Categoria da Shopee (ex.: shampoo, vitamina)" value={q} onChange={(e) => setQ(e.target.value)} />
                  </label>
                )}
              </div>
              {group && term.length >= 2 && (cats.isFetching && !cats.data ? <Loading /> : (
                <PickList items={cats.data?.items ?? []} keyOf={(c) => c.id} empty="Nenhuma categoria com esse nome."
                  label={(c) => <>{c.path} <span className="muted small">· {c.id}</span></>} onPick={(c) => !add.isPending && add.mutate(c.id)} />
              ))}
              {(add.error || del.error) && <ErrorBox error={add.error ?? del.error} />}
            </div>
          )}
        </div>
      )}
    </details>
  );
}

/** Situação da base de categorias da Shopee: lendo, falhou (com o motivo e o botão para tentar de novo) ou lida. */
function BaseStatus({ base, job, onRetry, busy }: { base: { categories: number; fetchedAt: string | null }; job: ResolveJob | null; onRetry: () => void; busy: boolean }) {
  const running = !!job && ['queued', 'running'].includes(job.status);
  const st = job?.stats as { specs?: number; total?: number } | null | undefined;
  if (running) return <div className="notice small">Lendo a base da Shopee em segundo plano{base.categories ? '' : ' (categorias)'}{st?.total ? `: especificações ${int(st.specs ?? 0)} de ${int(st.total)}` : '…'}</div>;
  if (job?.status === 'failed' && (!base.categories || !base.fetchedAt)) {
    return <div className="notice warn small">Não consegui ler a base de categorias da Shopee: {job.error ?? 'erro desconhecido'}. <button className="btn sm" type="button" disabled={busy} onClick={onRetry}>Tentar de novo</button></div>;
  }
  if (!base.categories) return <div className="notice small">A base de categorias da Shopee ainda não foi lida. <button className="btn sm" type="button" disabled={busy} onClick={onRetry}>Ler agora</button></div>;
  return null;
}

/** Mesma categoria para vários produtos. */
function BulkCategory({ accountId, productIds, onClose }: { accountId: string; productIds: string[]; onClose: (done: boolean) => void }) {
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  useEffect(() => { const t = setTimeout(() => setTerm(q.trim()), 250); return () => clearTimeout(t); }, [q]);
  const cats = useQuery({
    queryKey: ['shopee', 'cats', accountId, term],
    queryFn: () => api<{ items: { id: number; path: string }[]; loading?: boolean }>(`/channels/shopee/accounts/${accountId}/categories?q=${encodeURIComponent(term)}`),
    enabled: term.length >= 2,
  });
  const apply = useMutation({
    mutationFn: (categoryId: number) => api(`/channels/shopee/accounts/${accountId}/items/bulk`, { method: 'POST', json: { productIds, categoryId } }),
    onSuccess: () => onClose(true),
  });
  return (
    <Modal title={`Categoria para ${int(productIds.length)} produto(s)`} onClose={() => onClose(false)}>
      <label className="search" style={{ width: '100%' }}>
        <Search size={16} />
        <input className="input" autoFocus placeholder="Procurar categoria (ex.: perfume, shampoo, fralda)" value={q} onChange={(e) => setQ(e.target.value)} />
      </label>
      {cats.isFetching && !cats.data ? <Loading /> : cats.error ? <ErrorBox error={cats.error} /> : (
        <PickList items={cats.data?.items ?? []} keyOf={(c) => c.id} empty={cats.data?.loading ? 'A base de categorias da Shopee está sendo lida; tente de novo em instantes.' : term.length >= 2 ? 'Nenhuma categoria com esse nome.' : 'Digite parte do nome da categoria.'}
          label={(c) => <>{c.path} <span className="muted small">· {c.id}</span></>} onPick={(c) => apply.mutate(c.id)} />
      )}
      <span className="small muted">Os atributos digitados à mão nesses produtos são da categoria antiga e saem; os do cadastro são preenchidos de novo.</span>
      {apply.error && <ErrorBox error={apply.error} />}
    </Modal>
  );
}

/** Mesmo valor de atributo para vários produtos (ex.: Registro ANVISA, Volume, Gênero). */
function BulkAttribute({ accountId, productIds, onClose }: { accountId: string; productIds: string[]; onClose: (done: boolean) => void }) {
  const attrs = useQuery({
    queryKey: ['shopee', 'bulk-attrs', accountId, productIds.join(',')],
    queryFn: () => api<{ id: number; name: string; mandatory: boolean; free: boolean; values: { value_id: number; name: string }[]; missingIn: number }[]>(
      `/channels/shopee/accounts/${accountId}/items/attributes`, { method: 'POST', json: { productIds } }),
  });
  const [attrId, setAttrId] = useState<number | null>(null);
  const [value, setValue] = useState<{ value_id?: number; value_name?: string }>({});
  const a = attrs.data?.find((x) => x.id === attrId) ?? null;
  const apply = useMutation({
    mutationFn: () => api(`/channels/shopee/accounts/${accountId}/items/bulk`, { method: 'POST', json: { productIds, attribute: { id: attrId, ...value } } }),
    onSuccess: () => onClose(true),
  });
  return (
    <Modal title={`Preencher atributo em ${int(productIds.length)} produto(s)`} onClose={() => onClose(false)}>
      {attrs.isPending ? <Loading /> : attrs.error ? <ErrorBox error={attrs.error} /> : !attrs.data?.length ? (
        <p className="small muted" style={{ margin: 0 }}>Os produtos escolhidos ainda não têm categoria. Defina a categoria antes.</p>
      ) : (
        <>
          <div className="field">
            <label htmlFor="ba-attr">Atributo</label>
            <select id="ba-attr" className="select" value={attrId ?? ''} onChange={(e) => { setAttrId(e.target.value ? Number(e.target.value) : null); setValue({}); }}>
              <option value="">Escolha…</option>
              {attrs.data.map((x) => <option key={x.id} value={x.id}>{x.name}{x.mandatory ? ' *' : ''}{x.missingIn ? ` — falta em ${x.missingIn}` : ''}</option>)}
            </select>
          </div>
          {a && (
            <div className="field">
              <label htmlFor="ba-val">Valor</label>
              {a.values.length && !a.free ? (
                <select id="ba-val" className="select" value={value.value_id ?? ''} onChange={(e) => setValue(e.target.value ? { value_id: Number(e.target.value) } : {})}>
                  <option value="">Escolha…</option>
                  {a.values.map((v) => <option key={v.value_id} value={v.value_id}>{v.name}</option>)}
                </select>
              ) : (
                <>
                  <input id="ba-val" className="input" list="ba-list" value={value.value_name ?? ''} onChange={(e) => {
                    const t = e.target.value;
                    const hit = a.values.find((v) => v.name.toLowerCase() === t.trim().toLowerCase());
                    setValue(hit ? { value_id: hit.value_id, value_name: t } : { value_name: t });
                  }} />
                  {a.values.length > 0 && <datalist id="ba-list">{a.values.map((v) => <option key={v.value_id} value={v.name} />)}</datalist>}
                </>
              )}
            </div>
          )}
          <span className="small muted">Vale só para os produtos cuja categoria tem esse atributo. Deixe vazio para tirar o valor digitado.</span>
          {apply.error && <ErrorBox error={apply.error} />}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn" type="button" onClick={() => onClose(false)}>Cancelar</button>
            <button className="btn primary" type="button" disabled={!attrId || apply.isPending} onClick={() => apply.mutate()}>Aplicar</button>
          </div>
        </>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------------------------
// Publicações
// ---------------------------------------------------------------------------------------------

interface PubRow {
  id: string; status: string; price: number; quantity: number; itemId: string | null; categoryId: string | null; error: string | null;
  createdAt: string; sentAt: string | null; product: { id: string; sku: string; title: string | null };
}

export function ShopeePublications() {
  const { q: ov, account: a } = useShopee();
  const [params, set] = useUrlState();
  const status = params.get('status') ?? 'all';
  const [adjust, setAdjust] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['shopee', 'publications', a?.id, status],
    queryFn: () => api<PubRow[]>(`/channels/shopee/accounts/${a!.id}/publications?status=${status}`),
    enabled: !!a,
    refetchInterval: (query) => (query.state.data?.some((p) => p.status === 'queued' || p.status === 'sending') ? 3000 : false),
  });
  return (
    <>
      <Head title="Publicações na Shopee"><p>Cada produto que a Berytus publicou na Shopee. Recusados mostram o motivo dado pela Shopee.</p></Head>
      {ov.isPending ? <Loading /> : !a ? <NoShop /> : (
        <>
          <div className="toolbar">
            <div className="seg" role="group" aria-label="Situação">
              {([['all', 'Todas'], ['pending', 'Na fila'], ['published', 'Publicadas'], ['failed', 'Recusadas']] as const).map(([v, l]) => (
                <button key={v} type="button" aria-pressed={status === v} onClick={() => set({ status: v === 'all' ? null : v })}>{l}</button>
              ))}
            </div>
            <span className="grow" />
            <Link className="btn sm" to="/canal/shopee/publicar">Publicar mais produtos</Link>
          </div>
          <div className="panel flush">
            {q.error ? <div style={{ padding: 18 }}><ErrorBox error={q.error} /></div> : q.isPending ? <Loading /> : !q.data?.length ? (
              <div className="empty"><b>Nenhuma publicação</b>Publique em “Publicar produtos”.</div>
            ) : (
              <div className="grid-wrap">
                <table className="simple">
                  <thead><tr><th>Quando</th><th>Produto</th><th className="num">Preço</th><th className="num">Estoque</th><th>Situação</th><th>Anúncio</th></tr></thead>
                  <tbody>
                    {q.data.map((p) => (
                      <tr key={p.id}>
                        <td className="small">{dateTime(p.createdAt)}</td>
                        <td><Link to={`/catalogo/${p.product.id}`}>{p.product.title ?? p.product.sku}</Link><div className="small muted">SKU {p.product.sku}</div></td>
                        <td className="num">{brl(p.price)}</td>
                        <td className="num">{int(p.quantity)}</td>
                        <td style={{ maxWidth: 420 }}>
                          <Tag tone={PUB_STATUS[p.status]?.tone ?? 'off'}>{PUB_STATUS[p.status]?.label ?? p.status}</Tag>
                          {p.error && <div className="small" style={{ color: 'var(--err)', marginTop: 4 }}>{p.error}</div>}
                          {p.status === 'failed' && <button type="button" className="btn sm" style={{ marginTop: 6 }} onClick={() => setAdjust(p.product.id)}><Wrench size={13} /> Ajustar e publicar de novo</button>}
                        </td>
                        <td>{p.itemId ? <>nº {p.itemId}{p.categoryId && <div className="small muted">categoria {p.categoryId}</div>}</> : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <ShopeeAdjust accountId={a.id} productId={adjust} onClose={() => setAdjust(null)} />
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------------------------
// Ajustar o produto para a Shopee: categoria, marca da lista da Shopee e atributos obrigatórios
// ---------------------------------------------------------------------------------------------

interface DraftValue { value_id?: number; value_name?: string; unit?: string; multi?: { value_id?: number; value_name?: string }[] }
interface DraftAttr {
  id: number; name: string; mandatory: boolean; inputType: string; free: boolean; multi: boolean; search: boolean;
  validation: 'int' | 'string' | 'float' | 'date' | null; dateFormat: number | null; units: string[]; maxValues: number | null;
  values: { value_id: number; name: string; hasChildren: boolean }[]; value: DraftValue | null; source: 'manual' | 'auto' | null;
  parent: { attributeId: number; valueId: number } | null;
}
interface BrandChoice { brand_id: number; name: string }
interface Draft {
  product: { id: string; sku: string; title: string; brand: string | null; weightKg: number | null };
  category: { id: number; path: string | null; source: string } | null;
  suggestions: { id: number; path: string }[];
  brand: { mandatory: boolean; current: BrandChoice | null; source: string; missing: boolean; error?: string } | null;
  attributes: DraftAttr[];
  loadingSpecs?: boolean;
  gtin?: { code: string | null; rule: string | null; missing: boolean } | null;
  categoryError?: string | null;
  specsError?: string | null;
  missing: string[];
}

const BRAND_SOURCE: Record<string, string> = { product: 'escolhida para este produto', brand_map: 'escolhida para a marca toda', auto: 'encontrada pelo nome', none: '' };

/** Bloco numerado do ajuste: título, situação (ok / falta) e conteúdo. */
function Step({ n, title, ok, aside, children }: { n: number; title: string; ok: boolean; aside?: ReactNode; children: ReactNode }) {
  return (
    <section style={{ border: '1px solid var(--line)', borderRadius: 12, padding: 14, display: 'grid', gap: 10, background: 'var(--surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span aria-hidden style={{ width: 24, height: 24, borderRadius: 12, display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700,
          background: ok ? 'var(--ok-bg)' : 'var(--warn-bg)', color: ok ? 'var(--ok)' : 'var(--warn)' }}>{ok ? '✓' : n}</span>
        <h3 style={{ margin: 0, fontSize: 15 }}>{title}</h3>
        <span style={{ flex: 1 }} />
        {aside}
      </div>
      {children}
    </section>
  );
}

/** Lista de opções clicáveis (categorias, marcas), com rolagem. */
function PickList<T>({ items, keyOf, label, selected, onPick, empty }: { items: T[]; keyOf: (t: T) => string | number; label: (t: T) => ReactNode; selected?: string | number | null; onPick: (t: T) => void; empty: string }) {
  if (!items.length) return <div className="small muted" style={{ padding: '6px 2px' }}>{empty}</div>;
  return (
    <div role="listbox" style={{ display: 'grid', maxHeight: 220, overflow: 'auto', border: '1px solid var(--line)', borderRadius: 10 }}>
      {items.map((t, i) => {
        const on = selected != null && keyOf(t) === selected;
        return (
          <button key={keyOf(t)} type="button" role="option" aria-selected={on} onClick={() => onPick(t)}
            style={{ textAlign: 'left', padding: '8px 12px', border: 0, borderTop: i ? '1px solid var(--line)' : 0, cursor: 'pointer', font: 'inherit',
              background: on ? 'var(--brand-soft)' : 'transparent', color: 'inherit', fontWeight: on ? 600 : 400 }}>
            {label(t)}
          </button>
        );
      })}
    </div>
  );
}

export function ShopeeAdjust({ accountId, productId, onClose, onPublished }: { accountId: string; productId: string | null; onClose: () => void; onPublished?: () => void }) {
  const qc = useQueryClient();
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const [changingCat, setChangingCat] = useState(false);
  const [catSearch, setCatSearch] = useState('');
  const [brandSearch, setBrandSearch] = useState('');
  const [brand, setBrand] = useState<BrandChoice | null | undefined>(undefined);
  const [changingBrand, setChangingBrand] = useState(false);
  const [brandForAll, setBrandForAll] = useState(true);
  const [edits, setEdits] = useState<Record<string, DraftValue | null>>({});
  const [onlyRequired, setOnlyRequired] = useState(true);
  const open = !!productId;
  const reset = () => {
    setCategoryId(null); setChangingCat(false); setCatSearch(''); setBrandSearch(''); setBrand(undefined); setChangingBrand(false);
    setBrandForAll(true); setEdits({}); setOnlyRequired(true);
  };
  const close = () => { reset(); onClose(); };

  const [retrying, setRetrying] = useState(false);
  const draft = useQuery({
    queryKey: ['shopee', 'draft', accountId, productId, categoryId],
    // signal: fechar o modal ou trocar de item cancela a requisição em andamento.
    queryFn: ({ signal }) => api<Draft>(`/channels/shopee/accounts/${accountId}/products/${productId}/draft${categoryId ? `?categoryId=${categoryId}` : ''}`, { signal }),
    enabled: open,
    // Mantém a tela só entre prévias do MESMO produto (troca de categoria); de outro produto, nunca.
    placeholderData: (prev, prevQuery) => (prevQuery?.queryKey[3] === productId ? prev : undefined),
    // Atributos da categoria ainda sendo lidos no worker (ou relendo depois de uma falha): confere de novo em instantes.
    refetchInterval: (query) => (query.state.data?.loadingSpecs || retrying ? 3000 : false),
  });
  // Resposta de outro produto (chegou atrasada) é descartada.
  const d = draft.data && draft.data.product.id === productId ? draft.data : undefined;
  useEffect(() => { if (retrying && d && !d.specsError) setRetrying(false); }, [retrying, d]);
  const retrySpecs = useMutation({
    mutationFn: (catIdToRead: number) => api(`/channels/shopee/accounts/${accountId}/specs/refresh`, { method: 'POST', json: { categoryId: catIdToRead } }),
    onSuccess: () => setRetrying(true),
  });
  const catId = d?.category?.id ?? null;
  const cats = useQuery({
    queryKey: ['shopee', 'cats', accountId, catSearch.trim()],
    queryFn: () => api<{ items: { id: number; path: string }[]; loading?: boolean }>(`/channels/shopee/accounts/${accountId}/categories?q=${encodeURIComponent(catSearch.trim())}`),
    enabled: open && changingCat && catSearch.trim().length >= 2,
  });
  const [brandQ, setBrandQ] = useState('');
  useEffect(() => { const t = setTimeout(() => setBrandQ(brandSearch.trim()), 250); return () => clearTimeout(t); }, [brandSearch]);
  const shownBrand = brand === undefined ? d?.brand?.current ?? null : brand;
  const brandOpen = !!d?.brand && (changingBrand || (!shownBrand && d.brand.mandatory));
  const brands = useQuery({
    queryKey: ['shopee', 'brands', accountId, catId, brandQ],
    queryFn: () => api<{ mandatory: boolean; total: number; items: BrandChoice[]; error: string | null; loading?: boolean }>(`/channels/shopee/accounts/${accountId}/brands?categoryId=${catId}&q=${encodeURIComponent(brandQ)}`),
    enabled: open && !!catId && brandOpen,
    placeholderData: keepPreviousData,
    refetchInterval: (query) => (query.state.data?.loading ? 3000 : false),
  });

  const save = useMutation({
    mutationFn: async (publish: boolean) => {
      await api(`/channels/shopee/accounts/${accountId}/products/${productId}/draft`, {
        method: 'PUT',
        json: { ...(categoryId ? { categoryId } : {}), ...(brand !== undefined ? { brand, brandForAll } : {}), attributes: edits },
      });
      if (publish) return api<{ queued: number; skipped: DupSkip[] }>(`/channels/shopee/accounts/${accountId}/publish`, { method: 'POST', json: { productIds: [productId] } });
      return null;
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['shopee'] });
      if (r && r.queued) onPublished?.();
      if (!r || r.queued) close();
    },
  });

  const valueOf = (a: DraftAttr) => (a.id in edits ? edits[a.id] : a.value);
  const chosenIds = (a: DraftAttr) => { const v = valueOf(a); return (v?.multi ?? (v ? [v] : [])).map((x) => x.value_id).filter((x): x is number => !!x); };
  const filled = (a: DraftAttr) => { const v = valueOf(a); return !!(v?.value_id || v?.value_name?.trim() || v?.multi?.length) && validValue(a, v) === null; };
  // Árvore: atributo filho só vale (e só aparece) quando o valor de quem ele depende está escolhido.
  const byId = new Map((d?.attributes ?? []).map((a) => [a.id, a]));
  const active = (a: DraftAttr): boolean => !a.parent || (!!byId.get(a.parent.attributeId) && active(byId.get(a.parent.attributeId)!) && chosenIds(byId.get(a.parent.attributeId)!).includes(a.parent.valueId));
  const live = d?.attributes.filter(active) ?? [];
  const brandMissing = !!d?.brand?.mandatory && !shownBrand;
  const attrMissing = live.filter((a) => a.mandatory && !filled(a));
  const missingNow = [...(d?.categoryError ? ['Categoria'] : []), ...(d?.specsError ? ['Atributos (falha ao consultar a Shopee)'] : []), ...(brandMissing ? ['Marca'] : []), ...attrMissing.map((a) => a.name), ...(d?.gtin?.missing ? ['EAN'] : [])];
  const required = live.filter((a) => a.mandatory);
  const optional = live.filter((a) => !a.mandatory);
  const shownAttrs = onlyRequired ? required : [...required, ...optional];

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="drawer-overlay" />
        <Dialog.Content className="panel" aria-describedby={undefined}
          style={{ position: 'fixed', top: '4vh', left: '50%', transform: 'translateX(-50%)', width: 'min(820px, calc(100vw - 24px))', maxHeight: '92vh',
            zIndex: 60, padding: 0, display: 'grid', gridTemplateRows: 'auto 1fr auto', overflow: 'hidden' }}>
          <header style={{ padding: '16px 20px', borderBottom: '1px solid var(--line)', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <Dialog.Title asChild><h2 style={{ margin: 0, fontSize: 18 }}>Ajustar para a Shopee</h2></Dialog.Title>
              {d && <div className="small muted" style={{ marginTop: 4 }}>{d.product.title} · SKU {d.product.sku}{d.product.brand ? ` · ${d.product.brand}` : ''}</div>}
            </div>
            <Dialog.Close className="icon-btn" aria-label="Fechar"><X size={18} /></Dialog.Close>
          </header>

          <div style={{ overflow: 'auto', padding: 20, display: 'grid', gap: 14, alignContent: 'start', background: 'var(--bg)' }}>
            {draft.isPending ? <Loading /> : draft.error ? <ErrorBox error={draft.error} /> : d && (
              <>
                <Step n={1} title="Categoria" ok={!!d.category && !d.categoryError}
                  aside={!changingCat && <button type="button" className="btn sm" onClick={() => setChangingCat(true)}>Trocar</button>}>
                  {d.category ? (
                    <div>
                      <div style={{ fontWeight: 600 }}>{d.category.path ?? <>Categoria nº {d.category.id} <span className="small muted">(caminho em atualização: a árvore da Shopee é relida todo dia)</span></>}</div>
                      <div className="small muted">nº {d.category.id} · {ORIGIN[d.category.source] ?? (d.category.source === 'suggested' ? 'sugerida pela Shopee' : 'escolhida à mão')}</div>
                    </div>
                  ) : <div style={{ color: 'var(--warn)' }}>Sem categoria: escolha uma.</div>}
                  {d.categoryError && <div className="small" style={{ color: 'var(--err)' }}>{d.categoryError} — clique em Trocar e escolha outra.</div>}
                  {d.specsError && (
                    <div className="notice warn small">
                      Não consegui ler os atributos desta categoria na Shopee. Não é problema da categoria: tente de novo.
                      <div className="muted" style={{ marginTop: 4, wordBreak: 'break-word' }}>{d.specsError}</div>
                      <button type="button" className="btn sm" style={{ marginTop: 6 }} disabled={retrySpecs.isPending || retrying || !d.category}
                        onClick={() => d.category && retrySpecs.mutate(d.category.id)}>{retrying ? 'Lendo de novo…' : 'Tentar de novo'}</button>
                    </div>
                  )}
                  {changingCat && (
                    <div style={{ display: 'grid', gap: 8 }}>
                      {d.suggestions.length > 0 && (
                        <>
                          <span className="small muted">Sugestões da Shopee</span>
                          <PickList items={d.suggestions} keyOf={(c) => c.id} selected={d.category?.id} empty=""
                            label={(c) => <>{c.path} <span className="muted small">· {c.id}</span></>}
                            onPick={(c) => { setCategoryId(c.id); setEdits({}); setChangingCat(false); setCatSearch(''); }} />
                        </>
                      )}
                      <label className="search" style={{ width: '100%' }}>
                        <Search size={16} />
                        <input className="input" autoFocus placeholder="Procurar categoria (ex.: perfume, absorvente, shampoo)" value={catSearch} onChange={(e) => setCatSearch(e.target.value)} />
                      </label>
                      {catSearch.trim().length >= 2 && (cats.isFetching && !cats.data ? <Loading /> : cats.error ? <ErrorBox error={cats.error} /> : (
                        <PickList items={cats.data?.items ?? []} keyOf={(c) => c.id} selected={d.category?.id} empty={cats.data?.loading ? 'A base de categorias da Shopee está sendo lida; tente de novo em instantes.' : 'Nenhuma categoria com esse nome.'}
                          label={(c) => <>{c.path} <span className="muted small">· {c.id}</span></>}
                          onPick={(c) => { setCategoryId(c.id); setEdits({}); setChangingCat(false); setCatSearch(''); }} />
                      ))}
                      <div><button type="button" className="btn sm quiet" onClick={() => { setChangingCat(false); setCatSearch(''); }}>Cancelar</button></div>
                    </div>
                  )}
                </Step>

                {d.gtin && (
                  <div className={`notice small${d.gtin.missing ? ' warn' : ''}`}>
                    EAN: {d.gtin.code === '00' ? <b>sem EAN (a categoria aceita "00")</b> : d.gtin.code ? <b>{d.gtin.code}</b> : d.gtin.missing ? <b>falta — a Shopee exige EAN nesta categoria; cadastre o EAN no produto</b> : 'sem EAN (opcional nesta categoria)'}
                  </div>
                )}
                {d.loadingSpecs && <div className="notice small">Carregando os atributos e as marcas desta categoria da Shopee… a tela atualiza sozinha.</div>}
                {d.brand && (
                  <Step n={2} title="Marca" ok={!brandMissing}
                    aside={<>
                      {d.brand.mandatory && <Tag tone="info">obrigatória</Tag>}
                      {!brandOpen && <button type="button" className="btn sm" onClick={() => setChangingBrand(true)}>Trocar</button>}
                    </>}>
                    {d.brand.error && <div className="notice warn small">Não consegui carregar a lista de marcas: {d.brand.error}</div>}
                    <div>
                      {shownBrand ? (
                        <>
                          <div style={{ fontWeight: 600 }}>{shownBrand.brand_id === 0 ? 'Sem marca (NoBrand)' : shownBrand.name}</div>
                          {brand === undefined && d.brand.source !== 'none' && <div className="small muted">{BRAND_SOURCE[d.brand.source]}</div>}
                        </>
                      ) : d.brand.mandatory
                        ? <div style={{ color: 'var(--warn)' }}>A categoria exige uma marca da lista da Shopee, e "{d.product.brand ?? 'sem marca'}" não está nela com esse nome. Escolha abaixo.</div>
                        : <div>Sem marca (NoBrand)</div>}
                    </div>
                    {brandOpen && (
                      <div style={{ display: 'grid', gap: 8 }}>
                        <label className="search" style={{ width: '100%' }}>
                          <Search size={16} />
                          <input className="input" placeholder={`Filtrar as marcas da Shopee${d.product.brand ? ` (ex.: ${d.product.brand.split(' ')[0]})` : ''}`} value={brandSearch} onChange={(e) => setBrandSearch(e.target.value)} />
                        </label>
                        {brands.isPending ? <Loading /> : brands.error ? <ErrorBox error={brands.error} /> : (
                          <>
                            <PickList items={brands.data?.items ?? []} keyOf={(b) => b.brand_id} selected={shownBrand?.brand_id}
                              empty={brands.data?.total ? `Nenhuma das ${int(brands.data.total)} marcas desta categoria tem "${brandQ}". Tente outra parte do nome.` : brands.data?.error ?? 'A Shopee não devolveu marcas para esta categoria.'}
                              label={(b) => b.name}
                              onPick={(b) => { setBrand(b); setChangingBrand(false); }} />
                            <span className="small muted">{brands.data ? `${int(brands.data.total)} marcas nesta categoria${brands.data.items.length < brands.data.total ? `; mostrando ${int(brands.data.items.length)}, filtre pelo nome` : ''}.` : ''}</span>
                          </>
                        )}
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          {!d.brand.mandatory && <button type="button" className="btn sm" onClick={() => { setBrand({ brand_id: 0, name: 'NoBrand' }); setChangingBrand(false); }}>Publicar sem marca</button>}
                          {shownBrand && <button type="button" className="btn sm quiet" onClick={() => setChangingBrand(false)}>Cancelar</button>}
                        </div>
                      </div>
                    )}
                    {d.product.brand && brand && brand.brand_id !== 0 && (
                      <label className="small" style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 10px', background: 'var(--brand-soft)', borderRadius: 8 }}>
                        <input type="checkbox" className="check" checked={brandForAll} onChange={(e) => setBrandForAll(e.target.checked)} />
                        <span>Usar <b>{brand.name}</b> para todos os produtos da marca <b>{d.product.brand}</b> nesta loja</span>
                      </label>
                    )}
                  </Step>
                )}

                {d.attributes.length > 0 && (
                  <Step n={3} title="Atributos da categoria" ok={attrMissing.length === 0}
                    aside={optional.length > 0 && (
                      <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input type="checkbox" className="check" checked={!onlyRequired} onChange={(e) => setOnlyRequired(!e.target.checked)} />
                        mostrar os opcionais ({int(optional.length)})
                      </label>
                    )}>
                    {shownAttrs.length === 0 ? <div className="small muted">Esta categoria não tem atributo obrigatório.</div> : (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))', gap: 12 }}>
                        {shownAttrs.map((a) => (
                          <AttrField key={a.id} a={a} value={valueOf(a) ?? null} edited={a.id in edits} accountId={accountId} categoryId={catId}
                            parentName={a.parent ? byId.get(a.parent.attributeId)?.name ?? null : null}
                            onChange={(nv) => setEdits((e) => ({ ...e, [a.id]: nv }))} />
                        ))}
                      </div>
                    )}
                  </Step>
                )}
              </>
            )}
          </div>

          <footer style={{ padding: '12px 20px', borderTop: '1px solid var(--line)', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', background: 'var(--surface)' }}>
            <div className="small" style={{ flex: '1 1 260px', minWidth: 0 }}>
              {save.error ? <span style={{ color: 'var(--err)' }}>{(save.error as Error).message}</span>
                : save.data && save.data.queued === 0 && save.data.skipped.some((x) => x.reason === 'listed')
                  ? <DupNotice accountId={accountId} dups={save.data.skipped.filter((x) => x.reason === 'listed')} onDone={() => { onPublished?.(); close(); }} />
                : save.data && save.data.queued === 0 ? <span style={{ color: 'var(--warn)' }}>Não foi para a fila: {save.data.skipped.map((x) => x.label).join(', ')}.</span>
                : !d ? null
                : d.loadingSpecs ? <span className="muted">Carregando a categoria…</span>
                : missingNow.length ? <span style={{ color: 'var(--warn)' }}>Falta: <b>{missingNow.join(', ')}</b></span>
                : <span style={{ color: 'var(--ok)' }}>Pronto para publicar.</span>}
            </div>
            <button type="button" className="btn" disabled={save.isPending || !d} onClick={() => save.mutate(false)}>Salvar</button>
            <button type="button" className="btn primary" disabled={save.isPending || !d || !!d.loadingSpecs || missingNow.length > 0} onClick={() => save.mutate(true)}>
              <Send size={14} /> Salvar e publicar
            </button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

interface DupSkip { productId: string; sku: string; reason: string; label: string; existing?: { externalId: string; status: string | null; title: string | null; sku: string | null }[] }
const DUP_STATUS: Record<string, string> = { active: 'ativo', paused: 'pausado', under_review: 'em revisão' };

/** Produtos que não foram para a fila porque já existe anúncio do SKU na loja: mostra os anúncios e deixa publicar assim mesmo. */
function DupNotice({ accountId, dups, onDone }: { accountId: string; dups: DupSkip[]; onDone: (queued: number) => void }) {
  const qc = useQueryClient();
  const force = useMutation({
    mutationFn: () => api<{ queued: number }>(`/channels/shopee/accounts/${accountId}/publish`, { method: 'POST', json: { productIds: dups.map((d) => d.productId), allowDuplicates: true } }),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ['shopee'] }); onDone(r.queued); },
  });
  if (!dups.length) return null;
  return (
    <div className="notice warn small" style={{ display: 'grid', gap: 6 }}>
      <b>{dups.length === 1 ? 'Já existe anúncio para este SKU nesta loja' : `${dups.length} SKUs já têm anúncio nesta loja`} — não foram publicados:</b>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {dups.slice(0, 20).map((d) => (
          <li key={d.productId}>
            SKU <b>{d.sku}</b>: {(d.existing ?? []).map((e) => `anúncio ${e.externalId} (${DUP_STATUS[e.status ?? ''] ?? e.status ?? '—'})`).join(', ') || 'anúncio existente'}
          </li>
        ))}
        {dups.length > 20 && <li>e mais {dups.length - 20}…</li>}
      </ul>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <button className="btn sm" type="button" disabled={force.isPending}
          onClick={() => window.confirm(`Criar mais um anúncio para ${dups.length === 1 ? 'este SKU' : `estes ${dups.length} SKUs`} mesmo já existindo?`) && force.mutate()}>
          {force.isPending ? 'Enviando…' : 'Publicar assim mesmo'}
        </button>
        {force.error && <span style={{ color: 'var(--err)' }}>{(force.error as Error).message}</span>}
      </div>
    </div>
  );
}

/** Valor inválido para o tipo da Shopee (número inteiro, decimal, data): mensagem; válido ou vazio: null. */
function validValue(a: DraftAttr, v: DraftValue | null): string | null {
  const text = v?.value_id ? null : v?.value_name?.trim();
  if (!text) return null;
  if (a.validation === 'int' && !/^-?\d+$/.test(text)) return 'número inteiro';
  if (a.validation === 'float' && !/^-?\d+([.,]\d+)?$/.test(text)) return 'número';
  if (a.validation === 'date' && !(a.dateFormat === 1 ? /^\d{2}\/\d{4}$/ : /^\d{2}\/\d{2}\/\d{4}$/).test(text)) return a.dateFormat === 1 ? 'data MM/AAAA' : 'data DD/MM/AAAA';
  return null;
}

/**
 * Campo de um atributo da Shopee conforme o tipo: lista (um ou vários), lista que aceita texto, texto livre, número,
 * data, valor + unidade, e busca na Shopee para os atributos com muitos valores.
 */
function AttrField({ a, value, edited, onChange, accountId, categoryId, parentName }: {
  a: DraftAttr; value: DraftValue | null; edited: boolean; onChange: (v: DraftValue | null) => void; accountId: string; categoryId: number | null; parentName: string | null;
}) {
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  useEffect(() => { const t = setTimeout(() => setTerm(q.trim()), 300); return () => clearTimeout(t); }, [q]);
  const found = useQuery({
    queryKey: ['shopee', 'attr-values', accountId, categoryId, a.id, term],
    queryFn: ({ signal }) => api<{ items: { value_id: number; name: string }[] }>(`/channels/shopee/accounts/${accountId}/attribute-values?categoryId=${categoryId}&attributeId=${a.id}&q=${encodeURIComponent(term)}`, { signal }),
    enabled: a.search && !!categoryId && term.length >= 2,
  });
  const empty = !(value?.value_id || value?.value_name?.trim() || value?.multi?.length);
  const invalid = validValue(a, value);
  const warn = (a.mandatory && empty) || invalid ? { borderColor: 'var(--warn)', background: 'var(--warn-bg)' } : undefined;
  const nameOf = (x: { value_id?: number; value_name?: string }) => x.value_name ?? a.values.find((v) => v.value_id === x.value_id)?.name ?? String(x.value_id);
  const id = `attr-${a.id}`;
  const picks = value?.multi ?? (value && (value.value_id || value.value_name) ? [value] : []);
  const add = (x: { value_id?: number; value_name?: string }) => {
    if (!a.multi) return onChange(x.value_id || x.value_name ? { ...x, ...(value?.unit ? { unit: value.unit } : {}) } : null);
    if (picks.some((p) => (x.value_id && p.value_id === x.value_id) || (x.value_name && p.value_name === x.value_name))) return;
    if (a.maxValues && picks.length >= a.maxValues) return;
    onChange({ multi: [...picks.map((p) => ({ value_id: p.value_id, value_name: p.value_name })), x] });
  };
  const remove = (i: number) => { const rest = picks.filter((_, k) => k !== i); onChange(rest.length ? { multi: rest } : null); };
  const fromText = (t: string) => { const hit = a.values.find((v) => v.name.toLowerCase() === t.trim().toLowerCase()); return hit ? { value_id: hit.value_id, value_name: hit.name } : { value_name: t }; };

  return (
    <div className="field">
      <label htmlFor={id}>
        {a.name}{a.mandatory && <b style={{ color: 'var(--err)' }}> *</b>}
        {!edited && a.source === 'auto' && <span className="muted"> · do cadastro / catálogo mestre</span>}
        {parentName && <span className="muted"> · de {parentName}</span>}
      </label>
      {a.multi && picks.length > 0 && (
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {picks.map((p, i) => <Tag key={i} tone="info">{nameOf(p)} <button type="button" className="icon-btn" style={{ padding: 0 }} aria-label="Tirar" onClick={() => remove(i)}><X size={11} /></button></Tag>)}
        </div>
      )}
      {a.search ? (
        <>
          <input id={id} className="input" style={warn} placeholder={!a.multi && picks[0] ? nameOf(picks[0]) : 'Buscar na Shopee…'} value={q} onChange={(e) => setQ(e.target.value)} />
          {term.length >= 2 && (found.isFetching && !found.data ? <span className="small muted">Buscando…</span> : found.error ? <span className="small" style={{ color: 'var(--err)' }}>{(found.error as Error).message}</span> : (
            <div style={{ display: 'grid', gap: 2, maxHeight: 160, overflow: 'auto' }}>
              {(found.data?.items ?? []).map((v) => <button key={v.value_id} type="button" className="btn sm quiet" style={{ justifyContent: 'flex-start' }} onClick={() => { add({ value_id: v.value_id, value_name: v.name }); setQ(''); }}>{v.name}</button>)}
              {found.data && !found.data.items.length && <span className="small muted">Nada com "{term}".{a.free ? ' Pode digitar e usar o texto.' : ''}</span>}
              {a.free && <button type="button" className="btn sm quiet" style={{ justifyContent: 'flex-start' }} onClick={() => { add({ value_name: term }); setQ(''); }}>Usar "{term}"</button>}
            </div>
          ))}
        </>
      ) : a.values.length && !a.free ? (
        <select id={id} className="select" style={warn} value={a.multi ? '' : value?.value_id ?? ''}
          onChange={(e) => (e.target.value ? add({ value_id: Number(e.target.value) }) : !a.multi && onChange(null))}>
          <option value="">{a.multi ? 'Adicionar…' : 'Escolha…'}</option>
          {a.values.map((x) => <option key={x.value_id} value={x.value_id}>{x.name}{x.hasChildren ? ' ›' : ''}</option>)}
        </select>
      ) : (
        <div style={{ display: 'flex', gap: 6 }}>
          <input id={id} className="input" list={a.values.length ? `${id}-list` : undefined} style={{ ...warn, flex: 1 }}
            inputMode={a.validation === 'int' || a.validation === 'float' ? 'decimal' : undefined}
            placeholder={a.validation === 'date' ? (a.dateFormat === 1 ? 'MM/AAAA' : 'DD/MM/AAAA') : a.values.length ? 'Escolha ou digite' : 'Digite'}
            value={a.multi ? q : value?.value_name ?? (value?.value_id ? nameOf(value) : '')}
            onChange={(e) => (a.multi ? setQ(e.target.value) : onChange(e.target.value.trim() ? { ...fromText(e.target.value), ...(value?.unit ? { unit: value.unit } : {}) } : null))}
            onKeyDown={(e) => { if (a.multi && e.key === 'Enter' && q.trim()) { e.preventDefault(); add(fromText(q.trim())); setQ(''); } }} />
          {a.units.length > 0 && (
            <select className="select" aria-label="Unidade" style={{ width: 80 }} value={value?.unit ?? a.units[0]} onChange={(e) => onChange({ ...(value ?? {}), unit: e.target.value })}>
              {a.units.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          )}
          {a.values.length > 0 && <datalist id={`${id}-list`}>{a.values.map((x) => <option key={x.value_id} value={x.name} />)}</datalist>}
        </div>
      )}
      {invalid && <span className="small" style={{ color: 'var(--warn)' }}>Use {invalid}.</span>}
      {a.multi && a.maxValues ? <span className="small muted">até {a.maxValues} valor(es){a.free ? ' · Enter para adicionar o texto' : ''}</span> : null}
    </div>
  );
}
